import React, { useState, useRef, useEffect } from 'react';

const PatternLock = ({ onComplete, onCancel, mode = 'verify', currentPattern = null }) => {
  const [pattern, setPattern] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [confirmPattern, setConfirmPattern] = useState(null);
  const [error, setError] = useState('');
  const canvasRef = useRef(null);
  
  // Responsive canvas size
  const getCanvasSize = () => {
    const isMobile = window.innerWidth < 600;
    return isMobile ? Math.min(window.innerWidth - 80, 350) : 500;
  };
  
  const [canvasSize, setCanvasSize] = useState(getCanvasSize());
  
  const [dots] = useState([
    { id: 1, x: 100, y: 100 }, { id: 2, x: 250, y: 100 }, { id: 3, x: 400, y: 100 },
    { id: 4, x: 100, y: 250 }, { id: 5, x: 250, y: 250 }, { id: 6, x: 400, y: 250 },
    { id: 7, x: 100, y: 400 }, { id: 8, x: 250, y: 400 }, { id: 9, x: 400, y: 400 }
  ]);
  
  // Scale dots based on canvas size
  const getScaledDots = () => {
    const scale = canvasSize / 500;
    return dots.map(dot => ({
      ...dot,
      x: dot.x * scale,
      y: dot.y * scale
    }));
  };
  
  const scaledDots = getScaledDots();
  const [mousePos, setMousePos] = useState(null);

  const DOT_RADIUS = canvasSize / 20; // Responsive
  const ACTIVE_RADIUS = canvasSize / 14; // Responsive

  useEffect(() => {
    const handleResize = () => {
      setCanvasSize(getCanvasSize());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    drawPattern();
  }, [pattern, mousePos, canvasSize]);

  const drawPattern = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw lines
    if (pattern.length > 0) {
      ctx.strokeStyle = '#d4af37';
      ctx.lineWidth = canvasSize / 60;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      
      ctx.beginPath();
      const firstDot = scaledDots.find(d => d.id === pattern[0]);
      ctx.moveTo(firstDot.x, firstDot.y);
      
      for (let i = 1; i < pattern.length; i++) {
        const dot = scaledDots.find(d => d.id === pattern[i]);
        ctx.lineTo(dot.x, dot.y);
      }
      
      // Draw line to mouse if drawing
      if (isDrawing && mousePos) {
        ctx.lineTo(mousePos.x, mousePos.y);
      }
      
      ctx.stroke();
    }

    // Draw dots
    scaledDots.forEach(dot => {
      const isActive = pattern.includes(dot.id);
      const radius = isActive ? ACTIVE_RADIUS : DOT_RADIUS;
      
      // Outer circle
      ctx.beginPath();
      ctx.arc(dot.x, dot.y, radius, 0, Math.PI * 2);
      ctx.fillStyle = isActive ? 'rgba(212, 175, 55, 0.3)' : 'rgba(255, 255, 255, 0.1)';
      ctx.fill();
      ctx.strokeStyle = isActive ? '#d4af37' : 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = 3;
      ctx.stroke();
      
      // Inner circle
      ctx.beginPath();
      ctx.arc(dot.x, dot.y, canvasSize / 50, 0, Math.PI * 2);
      ctx.fillStyle = isActive ? '#d4af37' : 'rgba(255, 255, 255, 0.5)';
      ctx.fill();
    });
  };

  const getDotAtPosition = (x, y) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const canvasX = x - rect.left;
    const canvasY = y - rect.top;
    
    return scaledDots.find(dot => {
      const distance = Math.sqrt(
        Math.pow(canvasX - dot.x, 2) + Math.pow(canvasY - dot.y, 2)
      );
      return distance <= ACTIVE_RADIUS;
    });
  };

  const handleMouseDown = (e) => {
    const dot = getDotAtPosition(e.clientX, e.clientY);
    if (dot) {
      setIsDrawing(true);
      setPattern([dot.id]);
      setError('');
    }
  };

  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    
    const dot = getDotAtPosition(e.clientX, e.clientY);
    if (dot && !pattern.includes(dot.id)) {
      setPattern(prev => [...prev, dot.id]);
    }
  };

  const handleMouseUp = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    setMousePos(null);
    
    if (pattern.length < 4) {
      setError('Wzór musi zawierać minimum 4 punkty');
      setPattern([]);
      return;
    }

    if (mode === 'setup') {
      if (!confirmPattern) {
        setConfirmPattern(pattern);
        setPattern([]);
        setError('');
      } else {
        // Verify confirmation
        if (JSON.stringify(pattern) === JSON.stringify(confirmPattern)) {
          onComplete(pattern);
        } else {
          setError('Wzory się nie zgadzają. Spróbuj ponownie.');
          setPattern([]);
          setConfirmPattern(null);
        }
      }
    } else if (mode === 'verify') {
      // Verify against stored pattern
      if (JSON.stringify(pattern) === JSON.stringify(currentPattern)) {
        onComplete(pattern);
      } else {
        setError('Nieprawidłowy wzór');
        setPattern([]);
      }
    }
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    handleMouseDown({ clientX: touch.clientX, clientY: touch.clientY });
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    handleMouseMove({ clientX: touch.clientX, clientY: touch.clientY });
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    handleMouseUp();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.92)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10001,
      padding: '20px'
    }}>
      <div style={{
        background: 'linear-gradient(135deg, rgba(13,27,42,0.98) 0%, rgba(27,38,59,0.98) 100%)',
        borderRadius: window.innerWidth < 600 ? '16px' : '24px',
        padding: window.innerWidth < 600 ? '20px' : '40px',
        maxWidth: '600px',
        width: '100%',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 0 100px rgba(212, 175, 55, 0.2)',
        border: '2px solid rgba(212, 175, 55, 0.3)',
        textAlign: 'center'
      }}>
        <h2 style={{
          margin: '0 0 12px 0',
          fontSize: window.innerWidth < 600 ? '20px' : '28px',
          fontWeight: '800',
          color: '#d4af37',
          textShadow: '0 2px 10px rgba(212, 175, 55, 0.3)'
        }}>
          {mode === 'setup' ? '🔐 Ustaw wzór' : '🔓 Narysuj wzór'}
        </h2>
        
        <p style={{
          margin: '0 0 20px 0',
          color: 'rgba(255, 255, 255, 0.7)',
          fontSize: window.innerWidth < 600 ? '13px' : '15px'
        }}>
          {mode === 'setup' && !confirmPattern && 'Narysuj wzór (min. 4 punkty)'}
          {mode === 'setup' && confirmPattern && 'Potwierdź wzór - narysuj ponownie'}
          {mode === 'verify' && 'Aby kontynuować, narysuj wzór'}
        </p>

        <canvas
          ref={canvasRef}
          width={canvasSize}
          height={canvasSize}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            border: '2px solid rgba(212, 175, 55, 0.3)',
            borderRadius: '16px',
            cursor: 'pointer',
            touchAction: 'none',
            background: 'rgba(0, 0, 0, 0.3)',
            maxWidth: '100%',
            height: 'auto'
          }}
        />

        {error && (
          <div style={{
            marginTop: '20px',
            padding: '12px 20px',
            background: 'rgba(239, 68, 68, 0.2)',
            border: '1px solid rgba(239, 68, 68, 0.5)',
            borderRadius: '10px',
            color: '#fca5a5',
            fontSize: '14px',
            fontWeight: '600'
          }}>
            ❌ {error}
          </div>
        )}

        <div style={{
          marginTop: '24px',
          display: 'flex',
          gap: '12px',
          justifyContent: 'center'
        }}>
          <button
            onClick={() => { setPattern([]); setConfirmPattern(null); setError(''); }}
            style={{
              padding: '12px 24px',
              background: 'rgba(255,255,255,0.1)',
              border: '2px solid rgba(255,255,255,0.2)',
              borderRadius: '10px',
              color: 'white',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.15)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
          >
            🔄 Wyczyść
          </button>
          
          {onCancel && (
            <button
              onClick={onCancel}
              style={{
                padding: '12px 24px',
                background: 'rgba(239, 68, 68, 0.2)',
                border: '2px solid rgba(239, 68, 68, 0.4)',
                borderRadius: '10px',
                color: '#fca5a5',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(239, 68, 68, 0.3)';
                e.target.style.borderColor = 'rgba(239, 68, 68, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(239, 68, 68, 0.2)';
                e.target.style.borderColor = 'rgba(239, 68, 68, 0.4)';
              }}
            >
              ✕ Anuluj
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatternLock;
